import type {
  UserPreferences,
  ThemeMode,
  LanguageCode,
} from '@/types/userPreferences';
import { DEFAULT_PREFERENCES } from '@/types/userPreferences';

const STORAGE_KEY = 'user-preferences';

export const userPreferencesStorage = {
  /**
   * Get preferences for a specific user
   * Returns default if not found or userId mismatch
   */
  get(userId: string): UserPreferences {
    if (!userId) {
      return { userId: '', ...DEFAULT_PREFERENCES };
    }

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return { userId, ...DEFAULT_PREFERENCES };
      }

      const parsed: UserPreferences = JSON.parse(stored);

      // Validate userId match - if mismatch, it means another user was logged in
      if (parsed.userId !== userId) {
        // We return defaults but don't clear storage yet to avoid side effects during render
        return { userId, ...DEFAULT_PREFERENCES };
      }

      return parsed;
    } catch (error) {
      console.error('Failed to read user preferences:', error);
      return { userId, ...DEFAULT_PREFERENCES };
    }
  },

  /**
   * Update theme preference for current user
   */
  setTheme(userId: string, theme: ThemeMode): void {
    if (!userId) return;

    try {
      const current = this.get(userId);
      // Ensure we overwrite with correct userId if it was a mismatch
      const updated: UserPreferences = { ...current, userId, theme };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    } catch (error) {
      console.error('Failed to save theme preference:', error);
    }
  },

  /**
   * Update language preference (prepared for future)
   */
  setLanguage(userId: string, language: LanguageCode): void {
    if (!userId) return;

    try {
      const current = this.get(userId);
      const updated: UserPreferences = { ...current, userId, language };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    } catch (error) {
      console.error('Failed to save language preference:', error);
    }
  },

  /**
   * Clear preferences (optional, usually we keep them)
   */
  clear(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear preferences:', error);
    }
  },
};
